import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { routes } from '@/lib/routes';
import { portalApi } from '../api';
import type { ContentFilters, PublicContentListOut } from '../types';
import { ContentCard } from './ContentCard';
import { ContentDetailOverlay } from './ContentDetailOverlay';

const NEWS_CATS: Array<{ key: string; label: string; category?: string }> = [
  { key: 'all', label: 'Все' },
  { key: 'portal', label: 'Портал', category: 'portal' },
  { key: 'science', label: 'Наука', category: 'science' },
  { key: 'media', label: 'СМИ', category: 'media' },
  { key: 'updates', label: 'Обновления', category: 'updates' },
  { key: 'events', label: 'События', category: 'events' },
];

const HASH_CAT: Record<string, string> = {
  portal: 'portal',
  science: 'science',
  media: 'media',
  updates: 'updates',
  events: 'events',
};

export const NewsSection = ({ filterKey = '' }: { filterKey?: string }) => {
  const { t } = useI18n();
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const isOwner = Boolean(user && hasRole('SYSTEM_OWNER'));

  const initialCat = useMemo(() => {
    if (!filterKey || filterKey === 'all' || filterKey === 'search') return 'all';
    return HASH_CAT[filterKey] || 'all';
  }, [filterKey]);

  const [cat, setCat] = useState(initialCat);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('new');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PublicContentListOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    setCat(initialCat);
    setPage(1);
  }, [initialCat]);

  useEffect(() => {
    if (filterKey === 'search') searchRef.current?.focus();
  }, [filterKey]);

  const filters: ContentFilters = useMemo(() => {
    const chip = NEWS_CATS.find((c) => c.key === cat);
    return {
      kind: 'NEWS',
      sort,
      page,
      category: chip?.category,
    };
  }, [cat, sort, page]);

  const load = useCallback(() => {
    setLoading(true);
    portalApi
      .list({ ...filters, q: q || undefined })
      .then(setData)
      .finally(() => setLoading(false));
  }, [filters, q]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => {
    setCat('all');
    setQ('');
    setSort('new');
    setPage(1);
  };

  return (
    <div className="portal-section">
      <div className="public-hero">
        <div>
          <span className="public-eyebrow">Новости</span>
          <h2>Лента публикаций</h2>
          <p>События платформы, наука, СМИ и системные обновления.</p>
        </div>
        {isOwner ? (
          <button
            type="button"
            className="portal-small-action primary"
            onClick={() => navigate(`${routes.owner}/content`)}
          >
            + Новая публикация
          </button>
        ) : null}
      </div>

      <section className="portal-news-toolbar">
        <div className="portal-chips" role="tablist" aria-label="Категории новостей">
          {NEWS_CATS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={cat === item.key ? 'active' : undefined}
              onClick={() => {
                setCat(item.key);
                setPage(1);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="portal-news-search">
          <input
            ref={searchRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="Поиск по новостям, источнику или хэштегу"
          />
          <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
            <option value="new">{t('portal.filter.sort.new' as any)}</option>
            <option value="popular">{t('portal.filter.sort.popular' as any)}</option>
          </select>
        </div>
      </section>

      <section className="portal-result-head">
        <div>
          <strong>{data?.total ?? 0}</strong>
          <span>публикаций</span>
        </div>
        <button type="button" className="portal-small-action" onClick={reset}>
          Сбросить
        </button>
      </section>

      {loading ? (
        <div className="public-empty">Загрузка...</div>
      ) : !data || data.items.length === 0 ? (
        <div className="public-empty">{t('portal.filter.noResults' as any)}</div>
      ) : (
        <>
          <div className="public-results">
            {data.items.map((item) => (
              <ContentCard key={item.id} item={item} onClick={(i) => setOpenId(i.id)} />
            ))}
          </div>
          {data.has_more ? (
            <div className="portal-load-more">
              <button type="button" onClick={() => setPage((p) => p + 1)}>
                Загрузить ещё
              </button>
            </div>
          ) : null}
        </>
      )}

      {openId ? <ContentDetailOverlay contentId={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
};
